import type { DomainNormalizer, DomainNormalizerContext } from '../types';

/**
 * Domain normalizer for band scales (column charts).
 *
 * Output format: `string[]` representing category values for each bar
 *
 * ## Responsibilities
 *
 * 1. **Normalize zoom domains**: Convert any domain format to `string[]`
 *    - Passes through `string[]` unchanged
 *    - Converts `[number, number]` (from continuous scales) by finding all data points within range
 *
 * 2. **Calculate domain from data**: When `zoomDomain` is null
 *    - Extracts unique x-values from chart data as strings
 *    - Preserves order of appearance (important for categorical data)
 *
 * ## Cross-Chart Sync
 *
 * When a LineChart (continuous scale) zooms and sets a `[number, number]` domain like
 * `[1704067200000, 1704240000000]`, this normalizer converts it to a string array
 * containing all data points within that range for use in a synced ColumnChart.
 *
 * @example
 * const normalizer = new BandScaleDomainNormalizer();
 *
 * // With zoom domain (passthrough)
 * normalizer.normalize({ zoomDomain: ['a', 'b', 'c'], chartData, xDataKey: 'x' });
 * // Returns: ['a', 'b', 'c']
 *
 * // Converting from continuous scale domain
 * normalizer.normalize({ zoomDomain: [100, 200], chartData, xDataKey: 'x' });
 * // Returns: ['100', '150', '200'] (data points within range)
 *
 * // Without zoom domain (calculate from data)
 * normalizer.normalize({ zoomDomain: null, chartData, xDataKey: 'x' });
 * // Returns: ['100', '150', '200', '250', '300'] (all unique values)
 */
export class BandScaleDomainNormalizer implements DomainNormalizer {
  normalize(context: DomainNormalizerContext): string[] | null {
    const { zoomDomain, chartData, xDataKey } = context;

    // If zoomed, normalize the zoom domain to string[]
    if (zoomDomain && Array.isArray(zoomDomain) && zoomDomain.length > 0) {
      return this.normalizeZoomDomain(zoomDomain, chartData, xDataKey);
    }

    // Not zoomed - calculate domain from data
    return this.calculateDomainFromData(chartData, xDataKey);
  }

  /**
   * Normalizes a zoom domain to `string[]` format.
   *
   * - `string[]` → returned as-is (already in correct format)
   * - `[number, number]` → finds all data points within range, converts to strings
   *
   * @returns The normalized string array domain, or null if no valid categories found
   */
  private normalizeZoomDomain(
    zoomDomain: [number, number] | string[],
    chartData: DomainNormalizerContext['chartData'],
    xDataKey: DomainNormalizerContext['xDataKey'],
  ): string[] | null {
    // Already in string[] format - return as-is
    if (typeof zoomDomain[0] === 'string') {
      return zoomDomain as string[];
    }

    // Convert from [number, number] format (continuous scale domain)
    if (typeof zoomDomain[0] === 'number' && zoomDomain.length === 2) {
      const [minVal, maxVal] = zoomDomain as [number, number];
      const actualMin = Math.min(minVal, maxVal);
      const actualMax = Math.max(minVal, maxVal);

      // Find all x values from chartData that fall within the range
      const categoriesInRange: string[] = [];

      for (const datum of chartData) {
        const xValue = datum[xDataKey];
        if (typeof xValue === 'number' && xValue >= actualMin && xValue <= actualMax) {
          categoriesInRange.push(String(xValue));
        }
      }

      // Sort by numeric value to maintain chronological/numerical order
      categoriesInRange.sort((a, b) => Number(a) - Number(b));

      return categoriesInRange.length > 0 ? categoriesInRange : null;
    }

    return null;
  }

  /**
   * Calculates domain from chart data as unique string values.
   *
   * Preserves order of appearance from the data, which is important for:
   * - Categorical data where order matters (e.g., weekdays, months)
   * - Time-series data where chronological order should be maintained
   *
   * @returns Unique x-values as strings in order of first appearance, or null if no data
   */
  private calculateDomainFromData(
    chartData: DomainNormalizerContext['chartData'],
    xDataKey: DomainNormalizerContext['xDataKey'],
  ): string[] | null {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    // Extract x values and convert to strings
    const xValues = chartData
      .map((d) => d[xDataKey])
      .filter((v): v is string | number => v !== null && v !== undefined)
      .map((v) => String(v));

    if (xValues.length === 0) {
      return null;
    }

    // Return unique values in order of appearance (preserves data order)
    return [...new Set(xValues)];
  }
}
